import { Actions as INTEGRATION_ACTIONS } from "@/redux/actions/integrationActions";
import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { camelCase } from "lodash";
import { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import ReactSelect from "react-select";
import { Actions as TANDA_ACTIONS } from "./workforce/store/workforceActions";
import { useState } from "react";
import Tooltip from "@/components/common/tooltip/Tooltip";
import { LinkIcon } from "@/icons/solid";
import Spinner from "@/components/common/Spinner";

const linkEmployeeIntegrations = ["Tanda"];

const ImportEmployeeModal = ({
	modalStatus,
	closeModal,
	selectIntegration,
	employeeList,
	integrationList,
	getEmployeeLists,
	linkEmployees,
	agreements,
	autoMap,
	employeesNotFound,
	autoMapLoading,
}) => {
	const integrations = useMemo(() => {
		if (integrationList?.length > 0) return [...integrationList];
		return integrationList;
	}, [JSON.stringify(integrationList)]);

	const selectedIntegration = integrations?.find((item) => item.value === modalStatus)?.label;

	const [mappedEmployees, setMappedEmployee] = useState([]);

	const mapEmployee = (agreement, link) => {
		const mapped = {
			link,
			agreement,
		};
		setMappedEmployee([...mappedEmployees, mapped]);
	};

	const removeEmployee = (agreement) => {
		const mapped = mappedEmployees?.filter((mapped) => mapped?.agreement !== agreement);
		setMappedEmployee(mapped);
	};

	useEffect(() => {
		if (typeof modalStatus === "string") getEmployeeLists?.[modalStatus]?.();
		if (!modalStatus) setMappedEmployee([]);
	}, [modalStatus]);

	const sendRequest = linkEmployees?.[modalStatus];
	const autoMapRequest = autoMap?.[modalStatus];

	if (!modalStatus) return null;

	return (
		<div
			className="modal"
			tabIndex="-1"
			role="dialog"
			aria-hidden="true"
			style={{ display: "block", background: "rgba(0,0,0,0.4)", overflow: "auto" }}
		>
			<div
				className="modal-dialog"
				style={{ width: "50%" }}
			>
				<div className="modal-content">
					<div
						className="block block-themed block-transparent remove-margin-b"
						style={{ padding: 0 }}
					>
						<div
							className="block-header bg-primary-dark"
							style={{ padding: 20, position: "sticky", top: 0, zIndex: 50 }}
						>
							<ul className="block-options">
								<li>
									<button
										type="button"
										onClick={() => {
											closeModal();
										}}
									>
										<XMarkIcon className="h-6 w-6 mr-3 text-white" />
									</button>
								</li>
							</ul>
							<h3 className="block-title">
								Link {selectedIntegration ? `${selectedIntegration} ` : null}Employees
							</h3>
						</div>
						<div
							className="block-content"
							style={{ paddingBottom: "2rem" }}
						>
							{employeeList ? (
								<>
									{autoMapRequest ? (
										<div className="col-xs-12 pr-0 pl-0">
											<Tooltip
												as="button"
												type="button"
												className="btn btn-primary pull-right"
												content={
													<p style={{ color: "white", whiteSpace: "nowrap" }}>
														AutoMap attempts to link employees based on <br />
														their name, identification numbers or email. Any <br />
														employees that cannot be linked will be displayed below.
													</p>
												}
												disabled={agreements?.length == 0 || autoMapLoading}
												onClick={() => {
													autoMapRequest();
												}}
											>
												{autoMapLoading ? (
													<Spinner
														className="text-dark-gray animate-spin"
														style={{
															height: "2rem",
															verticalAlign: "middle",
															marginRight: "0.5rem",
														}}
													/>
												) : (
													<LinkIcon
														className="text-dark-gray"
														style={{
															height: "2rem",
															verticalAlign: "middle",
															marginRight: "0.5rem",
														}}
													/>
												)}
												AutoMap Employees
											</Tooltip>
										</div>
									) : null}

									<table className="js-table-sections table table-hover">
										<thead>
											<tr>
												<th>Name</th>
												<th>Link</th>
											</tr>
										</thead>
										<tbody>
											{agreements?.map((employee) => {
												return (
													<BulkLinkRow
														employeesNotFound={employeesNotFound}
														employee={employee}
														employeeList={employeeList}
														mapEmployee={mapEmployee}
														removeEmployee={removeEmployee}
													/>
												);
											})}
										</tbody>
									</table>
									<div className="col-xs-12 pr-0 pl-0">
										<button
											type="button"
											className="btn btn-default"
											onClick={() => {
												closeModal();
											}}
										>
											Cancel
										</button>
										<button
											type="button"
											className="btn btn-success pull-right"
											disabled={mappedEmployees?.length == 0}
											onClick={() => {
												sendRequest?.(mappedEmployees);
											}}
										>
											<LinkIcon
												className="text-dark-gray"
												style={{
													height: "2rem",
													verticalAlign: "middle",
													marginRight: "0.5rem",
												}}
											/>
											Link {mappedEmployees?.length || ""} Employee/s
										</button>
									</div>
								</>
							) : (
								<div className="col-xs-12">
									<label>Link Employees To</label>
									<ReactSelect
										options={integrations}
										onChange={(item) => {
											selectIntegration(item.value);
										}}
									/>
								</div>
							)}
							<div className="clearfix" />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

function BulkLinkRow({ employee, employeeList, mapEmployee, removeEmployee, employeesNotFound }) {
	const title = employee.title ? employee.title : "";
	const firstName = employee.firstName || employee.employeeName;
	const lastName = employee.lastName || employee.employeeSurname;
	const name = employee.name ? employee.name : `${firstName} ${lastName}`;

	const wasNotFound = employeesNotFound?.includes(employee.id);

	const displayName = [title, name].filter(Boolean).join(" ");

	return (
		<tr key={employee.id}>
			<td style={{ width: "33%" }}>
				<label htmlFor={`checkbox-${employee.id}`}>
					{displayName}
					{wasNotFound ? (
						<Tooltip content={`${name} could not be found by AutoMap`}>
							<ExclamationTriangleIcon
								className="h-7 w-7 text-primary"
								style={{ marginLeft: "0.5rem", verticalAlign: "middle" }}
							/>
						</Tooltip>
					) : null}
				</label>
			</td>
			<td>
				<ReactSelect
					options={employeeList}
					isClearable
					onChange={(selected) => {
						if (!selected) removeEmployee(employee.id);
						else mapEmployee(employee.id, selected.value);
					}}
				/>
			</td>
		</tr>
	);
}

const mapStateToProps = (state) => {
	const { bulkLinkModal } = state.integration;

	const integrationList = Object.entries(state.integration || {})?.reduce((prev, [field, curr]) => {
		Object.values(curr || {}).map((item) => {
			if (item.name) {
				prev.push({
					label: item.name === "Tanda" ? "Workforce" : item.name,
					value: camelCase(String(item.name)),
					path: field,
					isDisabled: !linkEmployeeIntegrations.includes(item.name),
				});
			}
		});
		return prev;
	}, []);

	const selected = integrationList?.find((item) => item.value === bulkLinkModal);
	const { employees, links, ignoredEmployees, employeesNotFound, autoMapLoading } =
		state.integration?.[selected?.path]?.[selected?.value] || {};

	const employeeList = employees
		?.filter((item) => {
			const ignored = ignoredEmployees?.find((ignored) => ignored?.link == item?.id);
			return !ignored;
		})
		?.map((employee) => ({
			label: employee.name,
			value: employee.id,
		}));

	const agreements = state.employees?.employeeListWithSalary?.filter((employee) => {
		const link = links?.find((link) => link?.agreement == employee?.id);
		return !link;
	});

	return {
		modalStatus: bulkLinkModal,
		integrationList,
		employeesNotFound,
		autoMapLoading,
		agreements,
		employeeList,
	};
};

const mapDispatchToProps = (dispatch) => ({
	closeModal: () => dispatch(INTEGRATION_ACTIONS.toggleBulkLinkModal()),
	selectIntegration: (integration) => dispatch(INTEGRATION_ACTIONS.toggleBulkLinkModal(integration)),
	getEmployeeLists: {
		tanda: () => dispatch(TANDA_ACTIONS.getWorkforceEmployees()),
	},
	linkEmployees: {
		tanda: (links) => dispatch(TANDA_ACTIONS.linkEmployees(links)),
	},
	autoMap: {
		tanda: () => dispatch(TANDA_ACTIONS.autoMapEmployees()),
	},
});

export default connect(mapStateToProps, mapDispatchToProps)(ImportEmployeeModal);
